import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { PackWithBuyButton } from '../PackWithBuyButton';
import type { SpecialEventCardPackGroup_cardPackGroup } from './__generated__/index.graphql';
import background from './assets/background.webp';

const Root = styled(Vertical)`
  width: 100%;
  height: 100%;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  padding: var(--double-unit);
  overflow: hidden;
  gap: var(--double-unit);
  background: url(${background}) no-repeat center / cover;
`;

type Props = {
  cardPackGroup: SpecialEventCardPackGroup_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, slug: string) => void;
};
export const SpecialEventCardPackGroup = ({
  cardPackGroup,
  onClickBuy,
}: Props) => {
  const { cardPacks } = cardPackGroup;
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const isTabletAndAbove = useIsTabletAndAbove();

  let itemToDisplay = 2;
  if (isTabletAndAbove) {
    itemToDisplay = 3;
  }
  if (isDesktopAndAbove) {
    itemToDisplay = 4;
  }

  return (
    <Root>
      <Vertical gap={0.5}>
        <BrandM>
          <FormattedMessage
            id="commonCard.SpecialEventCardPackGroup.title"
            defaultMessage="{eventName} Packs"
            values={{
              eventName: 'WNTR',
            }}
          />
        </BrandM>
        <LabelL
          className="text-gradient-color"
          style={
            {
              '--text-gradient-color': 'var(--c-gradient-gold)',
            } as CSSProperties
          }
        >
          <FormattedMessage
            id="commonCard.SpecialEventCardPackGroup.subtitle"
            defaultMessage="Special edition guaranteed"
          />
        </LabelL>
      </Vertical>
      <Scrollable
        itemToDisplay={Math.min(itemToDisplay, cardPacks.length)}
        overhang={6}
      >
        {cardPacks.map(cardPack => {
          return (
            <PackWithBuyButton
              key={cardPack.slug}
              cardPack={cardPack}
              cardPackGroupId={cardPackGroup.id}
              onClickBuy={onClickBuy}
              gap={3}
              scaleOnHover={false}
            />
          );
        })}
      </Scrollable>
    </Root>
  );
};
SpecialEventCardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment SpecialEventCardPackGroup_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        ...PackWithBuyButton_cardPack
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<SpecialEventCardPackGroup_cardPackGroup>,
};
